import type { IAccountingVendor } from 'pages/Admin/Accounting/AccountingVendors/types'
import { useEffect, useRef, useState } from 'react'
import { getAccountingVendorCategories, getAccountingVendors } from 'services'
import { Input2 } from 'stories/components'

import { PayeeSearchResults } from './PayeeSearchResults'

const RESULT_LIMIT = 15

export const PayeeSearch = ({
  value = '',
  title = 'Payee',
  required = false,
  error = '',
  history = false,
  additionalElements,
  onChange,
  onAddNew,
  showHistory,
}: {
  value?: string
  title?: string
  required?: boolean
  error?: string
  history?: boolean
  additionalElements?: JSX.Element | Function | null
  onChange: (value: string) => void
  onAddNew?: (name: string) => void
  showHistory?: () => void
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState(value || '')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [vendors, setVendors] = useState<IAccountingVendor[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [categories, setCategories] = useState<Record<number, string>>({})
  const skipSearchRef = useRef(false)

  useEffect(() => {
    setQuery(value || '')
  }, [value])

  useEffect(() => {
    getAccountingVendorCategories().then((res) => {
      const next: Record<number, string> = {}
      ;(res || []).forEach((item: any) => {
        next[item.id] = item.value
      })
      setCategories(next)
    })
  }, [])

  useEffect(() => {
    if (skipSearchRef.current) {
      skipSearchRef.current = false
      setDebouncedQuery('')
      return
    }
    const timeoutId = setTimeout(() => setDebouncedQuery(query.trim()), 400)
    return () => clearTimeout(timeoutId)
  }, [query])

  useEffect(() => {
    setActiveIndex(0)
  }, [debouncedQuery, vendors])

  useEffect(() => {
    if (!debouncedQuery || !isOpen) {
      setVendors([])
      setTotal(0)
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)

    getAccountingVendors({
      query: debouncedQuery,
      orderBy: 'name',
      orderDir: '1',
      pageNum: 0,
      count: RESULT_LIMIT,
      activeOnly: true,
    })
      .then((data) => {
        if (cancelled) return
        setVendors(data.results || [])
        setTotal(Number(data.totalCount) || 0)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [debouncedQuery, isOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const onAddNewVendor = (name: string) => {
    skipSearchRef.current = true
    setIsOpen(false)
    setVendors([])
    setTotal(0)
    onAddNew?.(name)
  }

  const onSelectVendor = (vendor: IAccountingVendor) => {
    skipSearchRef.current = true
    setIsOpen(false)
    setVendors([])
    setTotal(0)
    setQuery(vendor.name)
    onChange(vendor.name)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || !query.trim()) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (!vendors.length) return
      setActiveIndex((prev) => Math.min(prev + 1, vendors.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (!vendors.length) return
      setActiveIndex((prev) => Math.max(prev - 1, 0))
    } else if (event.key === 'Enter') {
      if (vendors[activeIndex]) {
        event.preventDefault()
        onSelectVendor(vendors[activeIndex])
      }
    } else if (event.key === 'Escape') {
      setIsOpen(false)
    }
  }

  const showResults = isOpen && query.trim().length > 0

  return (
    <div className="PayeeSearch-container relative" ref={containerRef}>
      <Input2
        type="text"
        title={title}
        required={required}
        error={error}
        value={query}
        history={history}
        showHistory={showHistory}
        additionalElements={additionalElements ?? null}
        onChange={(nextValue: string) => {
          setQuery(nextValue)
          onChange(nextValue)
          setIsOpen(true)
        }}
        onFocus={() => {
          if (query.trim()) setIsOpen(true)
        }}
        onKeyDown={onKeyDown}
      />
      {showResults && (
        <div className="absolute left-0 right-0 z-[70] mt-1">
          <PayeeSearchResults
            query={query}
            vendors={vendors}
            total={total}
            loading={loading}
            activeIndex={activeIndex}
            categories={categories}
            onSelect={onSelectVendor}
            onActiveIndexChange={setActiveIndex}
            onAddNew={onAddNew ? onAddNewVendor : undefined}
          />
        </div>
      )}
    </div>
  )
}
